// Live Check — "All live checks" history (00-ds-09 §9, D148, lists at scale).
// ~300 deterministic past checks so the history can page and search
// client-side. A check is not a chart record (§Live Check); real data is
// server-side (§8).

import { generate, pick, int, fullName, PAYERS, MOCK_NOW } from '@/components/lists';

export type CheckOutcome = 'Active' | 'Not found' | 'Terminated' | 'Partial';

export interface AllCheckRow {
  id: string;
  name: string;
  payer: string;
  memberId: string;
  dateIso: string;
  dateLabel: string;
  at: string; // "9:42 AM"
  outcome: CheckOutcome;
  by: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const BY = ['Ava Thompson', 'Ryan Cole', 'Maria Delgado', 'Front desk'] as const;
const OUTCOMES: CheckOutcome[] = ['Active', 'Active', 'Active', 'Partial', 'Not found', 'Terminated'];

function usDate(d: Date): string {
  return `${MONTHS[d.getMonth()] ?? 'Jan'} ${String(d.getDate())}, ${String(d.getFullYear())}`;
}
function timeLabel(r: () => number): string {
  const h24 = int(r, 8, 17);
  const m = int(r, 0, 59);
  const ap = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${String(h12)}:${String(m).padStart(2, '0')} ${ap}`;
}
function memberId(r: () => number): string {
  const L = 'ABCDEFGHJKLMNPRTUVWXYZ';
  return `${L[int(r, 0, L.length - 1)] ?? 'A'}${String(int(r, 10000000, 99999999))}`;
}

/** ~300 checks, weighted toward the last 7 days so the default window is a
 * usable, pageable set; newest first. */
export const CHECK_HISTORY: AllCheckRow[] = generate<AllCheckRow>(300, 0xc4ec, (r, i) => {
  const daysBack = r() < 0.55 ? int(r, 0, 6) : int(r, 7, 120);
  const d = new Date(MOCK_NOW);
  d.setDate(d.getDate() - daysBack);
  return {
    id: `lc-h-${String(i)}`,
    name: fullName(r),
    payer: pick(r, PAYERS),
    memberId: memberId(r),
    dateIso: d.toISOString().slice(0, 10),
    dateLabel: usDate(d),
    at: timeLabel(r),
    outcome: pick(r, OUTCOMES),
    by: pick(r, BY),
  };
}).sort((a, b) => (a.dateIso !== b.dateIso ? b.dateIso.localeCompare(a.dateIso) : 0));

/** ISO cutoff (inclusive) for the last 7 days, measured from MOCK_NOW. */
export function last7Cutoff(): string {
  const d = new Date(MOCK_NOW);
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
}
